import { Component, EventEmitter, inject, Output, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  NMFTAButton,
  NMFTAButtonComponent,
  NMFTADataPanelComponent,
  NMFTAIconComponent,
  NMFTATag,
  NMFTATagColorType,
  NMFTATagVariantType
} from '@nmfta/nmfta-ui';
import { DownloadJobsStore } from '@shared/stores/download-jobs.store';
import { Job, JobStatus } from '@shared/models/download-job.model';
import { DownloadJobsService } from '@shared/services/download-jobs.service';
import { ErrorModalService } from '@shared/services/error-modal.service';
import { ErrorStore } from '@shared/stores/error.store';
import { DialogComponent } from '@shared/components/organisms/dialog/dialog.component';
import { PdfViewerModalComponent } from '@shared/components/organisms/pdf-viewer-modal/pdf-viewer-modal.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';

type JobAction = 'download' | 'preview';

const JOB_STATUS_TAGS: Record<JobStatus, {
  label: string;
  color: NMFTATagColorType;
  variant: NMFTATagVariantType;
}> = {
  [JobStatus.Pending]: {
    label: 'Pending',
    color: 'accent',
    variant: 'outlined',
  },
  [JobStatus.Available]: {
    label: 'Available',
    color: 'accent',
    variant: 'contained',
  },
  [JobStatus.InProgress]: {
    label: 'In progress',
    color: 'accent',
    variant: 'outlined',
  },
  [JobStatus.Error]: {
    label: 'Error',
    color: 'error',
    variant: 'contained',
  },
  [JobStatus.NoContent]: {
    label: 'Error - No Content',
    color: 'error',
    variant: 'contained',
  },
};

@Component({
  selector: 'nmfta-downloads-panel',
  imports: [NMFTAButtonComponent, TranslatePipe, MatProgressSpinnerModule, NMFTAIconComponent, NMFTADataPanelComponent, DatePipe],
  templateUrl: './downloads-panel.component.html',
  styleUrl: './downloads-panel.component.scss',
})
export class DownloadsPanelComponent {
  private readonly downloadJobsStore = inject(DownloadJobsStore);
  private readonly downloadJobsService = inject(DownloadJobsService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly errorStore = inject(ErrorStore);
  private readonly errorModalService = inject(ErrorModalService);

  readonly jobs = this.downloadJobsStore.jobs;
  readonly isPolling = this.downloadJobsStore.isPolling;
  readonly activeJobAction = signal<{ jobId: string; action: JobAction } | null>(null);

  @Output() readonly closePanel = new EventEmitter<void>();

  private previewFileDialogRef?: MatDialogRef<DialogComponent>;

  close(): void {
    this.closePanel.emit();
  }

  protected getActions(job: Job): NMFTAButton[] {
    if (job.status !== JobStatus.Available || !job.id) {
      return [];
    }

    return [{
      id: 'preview',
      label: this.translate.instant('Generic_Preview'),
      leftIcon: 'visibility',
      disabled: this.isActionDisabled(job) || !job.fileName?.toLowerCase().endsWith('.pdf'),
      isLoading: this.isActionLoading(job, 'preview'),
      onClick: () => this.previewFile(job)
    }, {
      id: 'download',
      label: this.translate.instant('Generic_Download'),
      leftIcon: 'file_download',
      disabled: this.isActionDisabled(job),
      styleType: 'contained',
      isLoading: this.isActionLoading(job, 'download'),
      onClick: () => this.downloadFile(job)
    }];
  }

  protected getTag(job: Job): NMFTATag[] {
    const isRunning = job.status === JobStatus.Pending || job.status === JobStatus.InProgress;

    if (isRunning && job.statusDetail) {
      return [{
        label: job.statusDetail,
        color: 'accent',
        variant: 'outlined',
      }];
    }

    return [JOB_STATUS_TAGS[job.status]];
  }

  protected getIcon(job: Job): string {
    if (job.status === JobStatus.Error || job.status === JobStatus.NoContent) {
      return 'warning_amber';
    }

    if (job.fileName?.toLowerCase().endsWith('.zip')) {
      return 'folder_zip';
    } else if (job.fileName?.toLowerCase().endsWith('.pdf')) {
      return 'description';
    } else if (job.fileName?.toLowerCase().endsWith('.csv')) {
      return 'table_view';
    }

    return 'insert_drive_file';
  }

  async previewFile(job: Job): Promise<void> {
    const jobId = this.beginAction(job, 'preview');
    if (!jobId) {
      return;
    }

    try {
      const response = await this.downloadJobsService.getJobFile(jobId);

      if (!response.success || !response.data) {
        this.reportFileActionError(
          response.message,
          'The preview could not be loaded.',
        );
        return;
      }

      this.previewFileDialogRef = this.dialog.open(DialogComponent, {
        width: '75%',
        maxWidth: '69.5rem',
        height: '80%',
        maxHeight: '60rem',
        data: {
          title: this.translate.instant('Generic_PreviewFile'),
          icon: 'text_snippet',
          content: PdfViewerModalComponent,
          dataForComponent: {
            fileData: response.data,
            fileName: job.fileName ?? 'download.pdf',
          },
        },
      });
    } catch (error: unknown) {
      this.reportFileActionError(error, 'The preview could not be loaded.');
    } finally {
      this.finishAction(jobId, 'preview');
    }
  }

  async downloadFile(job: Job): Promise<void> {
    const jobId = this.beginAction(job, 'download');
    if (!jobId) {
      return;
    }

    try {
      await this.downloadJobsService.downloadJob(jobId, job.fileName ?? 'download');
    } catch (error: unknown) {
      this.reportFileActionError(error, 'The download could not be completed.');
    } finally {
      this.finishAction(jobId, 'download');
    }
  }

  isActionDisabled(job: Job): boolean {
    return job.status !== JobStatus.Available
      || !job.id
      || this.activeJobAction() !== null;
  }

  isActionLoading(job: Job, action: JobAction): boolean {
    const activeAction = this.activeJobAction();
    return activeAction?.jobId === job.id && activeAction?.action === action;
  }

  private beginAction(job: Job, action: JobAction): string | null {
    if (job.status !== JobStatus.Available || !job.id || this.activeJobAction()) {
      return null;
    }

    this.activeJobAction.set({ jobId: job.id, action });
    return job.id;
  }

  private finishAction(jobId: string, action: JobAction): void {
    const activeAction = this.activeJobAction();
    if (activeAction?.jobId === jobId && activeAction.action === action) {
      this.activeJobAction.set(null);
    }
  }

  private reportFileActionError(error: unknown, fallbackMessage: string): void {
    const message = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : fallbackMessage;

    this.errorStore.logError({
      success: false,
      code: 'DOWNLOAD_JOB_FILE_FAILED',
      message,
    });
    this.errorModalService.showErrorModal();
  }
}
